// Audits: the fleet board. Every audit page on disk, its latest gate run, what
// Airtable says about it, whether its FUP3 is owed, and the four buttons that
// run the engine's own scripts on the box as background jobs.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api } from "../services";
import type { AuditItem, AuditsBoard, Defined, Job } from "../services/types";
import { useSession } from "../components/SessionContext";
import { PageHead, SectionHead } from "../components/dash/PageHead";
import { StatTiles } from "../components/dash/StatTiles";
import { DefinitionsPanel } from "../components/dash/DefinitionsPanel";
import { DataTable, type Column } from "../components/dash/DataTable";
import { ConfirmStrip } from "../components/dash/ConfirmBar";
import { RunResults, isActiveStatus, useJobRunner } from "../components/dash/JobPanel";
import { formatIST, formatISTDate } from "../lib/readAgo";

const TILES = ["audits.pages", "audits.pass", "audits.fail", "audits.fup3_owed"];

type Filter = "all" | "notbuilt" | "failing" | "fup3";
type Action = "build" | "publish";
type Pending = { slug: string; action: Action; message: string | null; error: string | null };

const BUILD_TEXT =
  "Runs claude for 30 to 90 minutes on the box, syncs git, renders and verifies the page, publishes it to R2 on PASS, then commits and pushes the audit from the box.";
const PUBLISH_TEXT =
  "Publishes the page to R2. Refused unless the latest gate run is PASS and newer than the page.";

function builtCell(a: AuditItem): string {
  if (!a.page?.exists) return "not built";
  return a.page.build_date || formatISTDate(a.page.built_at) || "built";
}

function gatesCell(a: AuditItem): string {
  if (!a.gates) return "unverified";
  if (a.gates.result === "PASS") return "pass";
  return `fail, ${a.gates.fails?.length ?? 0} of ${a.gates.gate_count}`;
}

function airtableCell(a: AuditItem): string {
  if (!a.airtable) return "no record";
  return a.airtable.link_ok ? "stamped" : "not stamped";
}

function fup3Cell(a: AuditItem): string {
  if (a.fup3?.owed) return a.fup3.engine_ready ? "waiting" : `engine blocked: ${a.fup3.engine_block}`;
  if (a.airtable?.fup3_sent) return "sent";
  return "";
}

const JOB_COLUMNS = (open: (id: number) => void): Column<Job>[] => [
  { key: "started", label: "Started IST", cell: (j) => formatIST(j.started_at || j.created_at) },
  { key: "kind", label: "Kind", cell: (j) => j.kind },
  { key: "key", label: "Key", cell: (j) => j.key },
  { key: "status", label: "Status", cell: (j) => j.status },
  { key: "exit", label: "Exit code", num: true, cell: (j) => (typeof j.exit_code === "number" ? j.exit_code : "") },
  {
    key: "log", label: "Open log",
    cell: (j) => <button type="button" className="site" onClick={() => open(j.id)}>Open log</button>,
  },
];

export function Audits() {
  const session = useSession();
  const runner = useJobRunner();
  const [filter, setFilter] = useState<Filter>("all");
  const [find, setFind] = useState("");
  const [pending, setPending] = useState<Pending | null>(null);
  const isAdmin = session?.role === "admin";

  const boardQ = useQuery({
    queryKey: ["audits:board"],
    queryFn: async () => {
      const r = await api.audits.board();
      if (!r.ok) throw new Error(r.error);
      return r as Defined<AuditsBoard>;
    },
    staleTime: 60_000,
  });

  const jobsQ = useQuery({
    queryKey: ["jobs", "audits"],
    queryFn: async () => {
      const r = await api.jobs.list(undefined, undefined, 30);
      if (!r.ok) throw new Error(r.error);
      return (r.jobs ?? []).filter((j) => j.kind.startsWith("audit_"));
    },
    staleTime: 30_000,
    refetchInterval: (q) => ((q.state.data ?? []).some((j) => isActiveStatus(j.status)) ? 5000 : false),
  });

  const d = boardQ.data;
  const items = d?.items ?? [];
  const jobs = jobsQ.data ?? [];
  const buildRunning =
    items.some((a) => a.job?.kind === "audit_build" && isActiveStatus(a.job.status)) ||
    jobs.some((j) => j.kind === "audit_build" && isActiveStatus(j.status));

  const counts = useMemo(
    () => ({
      all: items.length,
      notbuilt: items.filter((a) => !a.page?.exists).length,
      failing: items.filter((a) => a.gates && a.gates.result !== "PASS").length,
      fup3: items.filter((a) => a.fup3?.owed).length,
    }),
    [items],
  );

  const rows = useMemo(() => {
    const needle = find.trim().toLowerCase();
    return items.filter((a) => {
      if (filter === "notbuilt" && a.page?.exists) return false;
      if (filter === "failing" && !(a.gates && a.gates.result !== "PASS")) return false;
      if (filter === "fup3" && !a.fup3?.owed) return false;
      if (needle && !a.slug.toLowerCase().includes(needle) && !(a.channel || "").toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [items, filter, find]);

  function refetchAll() {
    void boardQ.refetch();
    void jobsQ.refetch();
  }

  async function arm(slug: string, action: Action) {
    setPending({ slug, action, message: null, error: null });
    const r = action === "build" ? await api.audits.build(slug, false) : await api.audits.publish(slug, false);
    setPending((p) => {
      if (!p || p.slug !== slug || p.action !== action) return p;
      if (!r.ok) return { ...p, error: r.error };
      return { ...p, message: r.message || null };
    });
  }

  async function confirmPending() {
    if (!pending) return;
    const { slug, action } = pending;
    const title = action === "build" ? `Build ${slug}` : `Publish ${slug}`;
    await runner.run(title, () => (action === "build" ? api.audits.build(slug, true) : api.audits.publish(slug, true)));
    setPending(null);
    void jobsQ.refetch();
  }

  async function quick(slug: string, action: "verify" | "pdf") {
    await runner.run(action === "verify" ? `Verify ${slug}` : `PDF ${slug}`, () =>
      action === "verify" ? api.audits.verify(slug) : api.audits.pdf(slug),
    );
    void jobsQ.refetch();
  }

  const columns: Column<AuditItem>[] = [
    { key: "slug", label: "Slug", cell: (a) => a.slug },
    { key: "prospect", label: "Prospect", cell: (a) => a.channel || "" },
    { key: "built", label: "Built", cell: builtCell },
    { key: "gates", label: "Gates", cell: gatesCell },
    {
      key: "live", label: "Live",
      cell: (a) => (a.page?.exists && a.url ? <a href={a.url} target="_blank" rel="noreferrer">open</a> : ""),
    },
    { key: "airtable", label: "Airtable", cell: airtableCell },
    { key: "fup3", label: "FUP3", cell: fup3Cell },
    {
      key: "actions", label: "Actions",
      cell: (a) => {
        const rowJobActive = !!a.job && isActiveStatus(a.job.status);
        const busy = !!runner.busy || rowJobActive;
        return (
          <div className="acts">
            <button
              type="button"
              className="btn sm"
              disabled={!isAdmin || buildRunning || busy}
              title={buildRunning ? "A build is running." : !isAdmin ? "Admin only." : undefined}
              onClick={() => void arm(a.slug, "build")}
            >
              Build
            </button>
            <button type="button" className="btn sm" disabled={busy || !a.page?.exists} onClick={() => void quick(a.slug, "verify")}>
              Verify
            </button>
            <button type="button" className="btn sm" disabled={busy || !a.page?.exists} onClick={() => void arm(a.slug, "publish")}>
              Publish
            </button>
            <button type="button" className="btn sm" disabled={busy || !a.page?.exists} onClick={() => void quick(a.slug, "pdf")}>
              PDF
            </button>
            {rowJobActive ? (
              <button type="button" className="site" onClick={() => runner.setJobId(a.job!.id)}>
                Watch job {a.job!.id}
              </button>
            ) : null}
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHead
        title="Audits"
        caption="Every audit page on disk, its latest gate run, and what Airtable says about it. Build, Verify, Publish and PDF run the engine's own scripts on the box."
      />

      <section className="section compact">
        <SectionHead title="Today" />
        {boardQ.isError ? (
          <div className="note-neg"><p>{(boardQ.error as Error).message}</p></div>
        ) : !d ? (
          <p className="empty">Reading the fleet.</p>
        ) : (
          <StatTiles
            tiles={[
              { id: "audits.pages", value: d.summary?.pages },
              { id: "audits.pass", value: d.summary?.pass },
              { id: "audits.fail", value: d.summary?.fail },
              { id: "audits.fup3_owed", value: d.summary?.fup3_owed },
            ]}
            definitions={d.definitions}
            readAt={d.read_at}
          />
        )}
      </section>

      <section className="section compact">
        <SectionHead
          title="Board"
          right={
            <div className="filters">
              <div className="kws">
                {(
                  [
                    ["all", `All ${counts.all}`],
                    ["notbuilt", `Not built ${counts.notbuilt}`],
                    ["failing", `Failing ${counts.failing}`],
                    ["fup3", `FUP3 waiting ${counts.fup3}`],
                  ] as [Filter, string][]
                ).map(([k, label]) => (
                  <button key={k} type="button" className={`kw${filter === k ? " on" : ""}`} onClick={() => setFilter(k)}>
                    {label}
                  </button>
                ))}
              </div>
              <input
                type="search"
                placeholder="Find a slug"
                aria-label="Find a slug"
                value={find}
                onChange={(e) => setFind(e.target.value)}
              />
            </div>
          }
        />
        {buildRunning ? <p className="sub-block">A build is running. Other builds wait until it finishes.</p> : null}
        {boardQ.isError ? null : (
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(a) => a.slug}
            loading={boardQ.isPending}
            loadingText="Reading the fleet."
            empty={items.length === 0 ? "No audits built yet." : "Nothing under this filter."}
            after={(a) =>
              pending && pending.slug === a.slug ? (
                <ConfirmStrip
                  confirmLabel={pending.action === "build" ? `Yes, build ${a.slug}` : `Yes, publish ${a.slug}`}
                  busy={!!runner.busy}
                  onCancel={() => setPending(null)}
                  onConfirm={confirmPending}
                >
                  {pending.error ? (
                    <div className="note-neg"><p>{pending.error}</p></div>
                  ) : (
                    <p>{pending.message || (pending.action === "build" ? BUILD_TEXT : PUBLISH_TEXT)}</p>
                  )}
                </ConfirmStrip>
              ) : null
            }
          />
        )}
      </section>

      <section className="section compact">
        <SectionHead title="Current job" />
        {runner.jobId == null && !runner.out && !runner.err ? (
          <p className="empty">No job is open. Start one from the board, or open a log below.</p>
        ) : null}
        <RunResults runner={runner} onFinish={refetchAll} />
      </section>

      <section className="section compact">
        <SectionHead title="Recent jobs" />
        {jobsQ.isError ? (
          <div className="note-neg"><p>{(jobsQ.error as Error).message}</p></div>
        ) : (
          <DataTable
            columns={JOB_COLUMNS((id) => runner.setJobId(id))}
            rows={jobs}
            rowKey={(j) => String(j.id)}
            loading={jobsQ.isPending}
            loadingText="Reading the job list."
            empty="No jobs have run."
          />
        )}
      </section>

      {d ? <DefinitionsPanel ids={TILES} definitions={d.definitions} readAt={d.read_at} /> : null}
    </>
  );
}
